import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Icon,
  InlineMessage,
  InputField,
  Typography,
} from '@saviynt/design-system';

import { FormLayout } from '../../../shared/components';
import { type IdentityProvider } from '../../../shared/types';
import { loadUsernameData, saveUsernameData } from '../data-sources';

import './ProviderChooserForm.css';

const CONTINUE_BUTTON = '##CONTINUE##';

interface Props {
  welcomeMessage: string;
  versionMessage: string;
  providers: IdentityProvider[];
  onChoose: (props: { alias: string } | { username: string }) => void;
  isError: boolean;
  isLoading: boolean;
  errorMessage?: string;
}

/**
 * Login form displayed when multiple identity providers are available.
 * Allows the user to enter a username or choose an SSO provider to authenticate with.
 */
function ProviderChooserForm({
  welcomeMessage,
  versionMessage,
  providers,
  onChoose,
  isError,
  isLoading,
  errorMessage,
}: Props): JSX.Element {
  const saved = loadUsernameData();
  const [shoudRemember, setShouldRemember] = useState(saved.shoudRemember);
  const [username, setUsername] = useState(saved.username);
  const [loadingButton, setLoadingButton] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingButton(CONTINUE_BUTTON);

    if (!username) {
      return;
    }

    saveUsernameData({ shoudRemember, username });
    onChoose({ username });
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldRemember(e.target.checked);
    saveUsernameData({ shoudRemember: e.target.checked, username });
  };

  const footer = (
    <>
      <Box className='Auth_ProviderChooserForm-lineWithText'>
        <Typography kind='body2'>Or Sign in With</Typography>
      </Box>
      <Box className='Auth_ProviderChooserForm-buttonGroup'>
        {providers.map((provider) => (
          <Button
            kind='outlined'
            key={provider.alias}
            isLoading={loadingButton === provider.alias && isLoading}
            onClick={() => {
              setLoadingButton(provider.alias);
              onChoose({ alias: provider.alias });
            }}
            className='Auth_ProviderChooserForm-button'>
            {provider.name}
          </Button>
        ))}
      </Box>
    </>
  );

  return (
    <FormLayout
      title='Login faster and safer to your account.'
      subtitle={welcomeMessage}
      versionMessage={versionMessage}
      footerContent={providers.length > 0 ? footer : null}
      onSubmit={handleSubmit}>
      <InputField
        name='username'
        placeholder='Enter Your Username'
        value={username}
        setValue={setUsername}
      />
      <Checkbox
        className='Auth_ProviderChooserForm-rememberMeCheckbox'
        showHoverEffect
        isChecked={shoudRemember}
        onChange={handleToggle}
        textPosition='after'
        label='Remember me'
      />
      {isError && (
        <InlineMessage
          kind='background'
          text='Color Theme Critical'
          colorTheme='critical'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
          className='Auth_ProviderChooserForm-errorField'>
          <>
            <Typography kind='body1-bold'>
              {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string should also fall back */}
              {errorMessage || 'Unexpected Error'}
            </Typography>
            <Typography kind='body2'>Please try again.</Typography>
          </>
        </InlineMessage>
      )}
      <Button
        type='submit'
        kind='filled'
        isDisabled={!username}
        isLoading={loadingButton === CONTINUE_BUTTON && isLoading}
        loaderText='Loading'
        className='Auth_ProviderChooserForm-button'>
        Continue
      </Button>
    </FormLayout>
  );
}

export default ProviderChooserForm;
